import { useCallback } from 'react';

import {
   CurrentHealthcareProviderEwusConfigurationQuery,
   HealthcareProviderEwusConfiguration,
} from '@dreryk/egabinet-ewus-frontend-api';
import {
   CurrentProviderEzwmConfigurationQuery,
   HealthcareProviderEzwmConfiguration,
} from '@dreryk/egabinet-ezwm-frontend-api';
import {
   CurrentHealthcareProviderNfzConfigurationQuery,
   CurrentPractitionerNfzConfigurationQuery,
   HealthcareProviderNfzConfiguration,
   PractitionerNfzConfiguration,
} from '@dreryk/egabinet-nfz-configuration-frontend-api';

import {
   CqrsQueryPerformResult,
   performCqrsQueryFind,
   useCqrsFind,
   UseCqrsQueryResult,
} from '@dreryk/command-query-react-common';
import { useFormController } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';

import { nfzLoginTranslationKeys } from '../../../translations';

import { NfzLoginFormBean, NfzLoginFormController } from './NfzLoginForm.types';
import { getEzwmMfaInitialValues } from './NfzLoginForm.utils';
import { MFA_SECRET_PLACEHOLDER_VALUE } from './fields/MfaSecretField';

type NfzLoginFormControllerOptions = {
   currentPractitionerNfzConfiguration: PractitionerNfzConfiguration | null;
   ewusEnabled: boolean;
   ezwmEnabled: boolean;
};

export const useNfzLoginFormController = (options: NfzLoginFormControllerOptions): NfzLoginFormController => {
   const getInitialValues = (): NfzLoginFormBean => {
      if (!options.currentPractitionerNfzConfiguration) {
         return {
            personnelCode: null,
            login: null,
            password: null,
            passwordChangeEnabled: false,
            ...(options.ewusEnabled
               ? {
                    ewusEnabled: true,
                    mfaStrategy: null,
                 }
               : {
                    ewusEnabled: false,
                    ...(options.ezwmEnabled
                       ? {
                            ezwmEnabled: true,
                            mfaStrategy: null,
                         }
                       : {
                            ezwmEnabled: false,
                         }),
                 }),
         };
      }

      const { mfaSecretPresent } = options.currentPractitionerNfzConfiguration;

      return {
         personnelCode: options.currentPractitionerNfzConfiguration.personnelCode ?? null,
         login: options.currentPractitionerNfzConfiguration.login,
         password: null,
         passwordChangeEnabled: false,
         ...(options.ewusEnabled
            ? {
                 ewusEnabled: true,
                 ...(mfaSecretPresent
                    ? {
                         mfaStrategy: 'SECRET',
                         mfaSecret: mfaSecretPresent ? MFA_SECRET_PLACEHOLDER_VALUE : null,
                      }
                    : {
                         mfaStrategy: 'TOTP',
                         mfaTotp: null,
                      }),
              }
            : {
                 ewusEnabled: false,
                 ...(options.ezwmEnabled
                    ? {
                         ezwmEnabled: true,
                         ...getEzwmMfaInitialValues(mfaSecretPresent),
                      }
                    : {
                         ezwmEnabled: false,
                      }),
              }),
      };
   };

   return useFormController<NfzLoginFormBean>(getInitialValues(), {
      resetAfterSubmit: true,
   });
};

export const useCurrentPractitionerNfzConfigurationQuery = (): UseCqrsQueryResult<PractitionerNfzConfiguration | null> => {
   return useCqrsFind(() => {
      return new CurrentPractitionerNfzConfigurationQuery();
   });
};

export const useCurrentPractitionerNfzConfigurationDeferredQuery = (): (() => CqrsQueryPerformResult<PractitionerNfzConfiguration | null>) => {
   return useCallback(
      // eslint-disable-next-line @typescript-eslint/require-await
      async () =>
         performCqrsQueryFind<PractitionerNfzConfiguration>(() => new CurrentPractitionerNfzConfigurationQuery()),
      []
   );
};

export const useHealthcareProviderNfzConfigurationQuery = (): UseCqrsQueryResult<HealthcareProviderNfzConfiguration | null> => {
   return useCqrsFind(() => new CurrentHealthcareProviderNfzConfigurationQuery());
};

export const useHealthcareProviderNfzConfigurationDeferredQuery = (): (() => CqrsQueryPerformResult<HealthcareProviderNfzConfiguration | null>) => {
   return useCallback(
      // eslint-disable-next-line @typescript-eslint/require-await
      async () =>
         performCqrsQueryFind<HealthcareProviderNfzConfiguration>(
            () => new CurrentHealthcareProviderNfzConfigurationQuery()
         ),
      []
   );
};

export const useCurrentHealthcareProviderEwusConfigurationQuery = (): UseCqrsQueryResult<HealthcareProviderEwusConfiguration | null> => {
   return useCqrsFind(() => new CurrentHealthcareProviderEwusConfigurationQuery());
};

export const useCurrentProviderEzwmConfigurationQuery = (): UseCqrsQueryResult<HealthcareProviderEzwmConfiguration | null> => {
   return useCqrsFind<HealthcareProviderEzwmConfiguration>(() => new CurrentProviderEzwmConfigurationQuery());
};

export const useCurrentProviderEzwmConfigurationDeferredQuery = (): (() => CqrsQueryPerformResult<HealthcareProviderEzwmConfiguration | null>) => {
   return useCallback(
      // eslint-disable-next-line @typescript-eslint/require-await
      async () =>
         performCqrsQueryFind<HealthcareProviderEzwmConfiguration>(() => new CurrentProviderEzwmConfigurationQuery()),
      []
   );
};

type UseEzwmEnabledCheckerReturn = () => Promise<boolean>;
export const useEzwmEnabledChecker = (): UseEzwmEnabledCheckerReturn => {
   const fetchCurrentHealthcareProviderNfzConfiguration = useHealthcareProviderNfzConfigurationDeferredQuery();
   const fetchCurrentPractitionerNfzConfiguration = useCurrentPractitionerNfzConfigurationDeferredQuery();
   const fetchCurrentHealthcareProviderEzwmConfiguration = useCurrentProviderEzwmConfigurationDeferredQuery();
   return async () => {
      try {
         const [healthcareProviderNfzConfiguration, practitionerNfzConfiguration, ezwmEnabled] = await Promise.all([
            fetchCurrentHealthcareProviderNfzConfiguration(),
            fetchCurrentPractitionerNfzConfiguration(),
            fetchCurrentHealthcareProviderEzwmConfiguration(),
         ]);
         return Boolean(healthcareProviderNfzConfiguration && practitionerNfzConfiguration && ezwmEnabled);
      } catch {
         dispatchSnackbarNegativeEvent(nfzLoginTranslationKeys.errorWhileFetchingNfzConfiguration);
         return false;
      }
   };
};
